// src/pages/reports/StaffFteReport.tsx
//
// Staff FTE (time fraction) report. Lists every (active) staff member with
// their contracted FTE and an effective FTE derived from the teaching load
// allocated to them for the chosen semester. Defaults to Semester 2.

import React, { useEffect, useMemo, useState } from 'react';
import { Users } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Card from '../../components/ui/Card';
import { TableSkeleton } from '../../components/ui/Skeleton';
import EmptyState from '../../components/ui/EmptyState';
import { useToast } from '../../contexts/ToastContext';
import { useData } from '../../contexts/DataContext';
import { gridsService } from '../../services/gridsService';
import { getLoadLetterSettings } from '../../services/loadLetterService';
import {
  getStaffFteList,
  type StaffFteRow,
  type FteSemester,
} from '../../services/staffFteReportService';
import { exportReport, Formatters, type ExportData } from '../../utils/reportExport';
import { displayLearningArea } from '../../constants/learningAreas';

// Default to the active academic period: Semester 2 from May onwards, else
// Semester 1. Mirrors the load-letter pages and the Timetabled Subs report.
const currentSemester = (): FteSemester =>
  new Date().getMonth() >= 4 ? 'semester2' : 'semester1';

const semesterLabel = (s: FteSemester): string =>
  s === 'semester1' ? 'Semester 1' : 'Semester 2';

const fmtFte = (v: number | null): string => (v == null ? '—' : v.toFixed(2));
const fmtDelta = (v: number | null): string =>
  v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(2)}`;
const fmtMins = (v: number | null): string => (v == null ? '—' : String(v));

const StaffFteReport: React.FC = () => {
  const { addToast } = useToast();
  // The roster is already loaded here for the department list; reuse it to tell
  // "no staff exist" apart from "the filters excluded everyone" (issue #152).
  const { departments, staff } = useData();

  const [years, setYears] = useState<number[]>([]);
  const [year, setYear] = useState<number | null>(null);
  const [department, setDepartment] = useState<string>('all');
  const [semester, setSemester] = useState<FteSemester>(currentSemester);
  const [activeOnly, setActiveOnly] = useState(true);
  const [rows, setRows] = useState<StaffFteRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState<keyof StaffFteRow>('totalFte');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    (async () => {
      try {
        const [available, settings] = await Promise.all([
          gridsService.getAvailableYears(),
          getLoadLetterSettings(),
        ]);
        setYears(available);
        const defaultYear = settings?.currentAcademicYear
          ? Number(settings.currentAcademicYear)
          : available[0] ?? new Date().getFullYear();
        setYear(defaultYear);
      } catch (err) {
        console.error('Failed to load year options:', err);
        addToast('Could not load year options', 'error');
      }
    })();
  }, [addToast]);

  useEffect(() => {
    if (year == null) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const result = await getStaffFteList({ year, department, activeOnly, semester });
        if (!cancelled) setRows(result);
      } catch (err) {
        console.error('Staff FTE report failed:', err);
        if (!cancelled) addToast('Failed to generate report', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [year, department, activeOnly, semester, addToast]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];

      // Push null/undefined to the end regardless of direction.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'desc' ? bv - av : av - bv;
      }
      const as = String(av);
      const bs = String(bv);
      return sortDir === 'desc' ? bs.localeCompare(as) : as.localeCompare(bs);
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const withLetters = useMemo(
    () => sortedRows.filter(r => r.hasLetter).length,
    [sortedRows]
  );

  // Column totals. Contracted FTE sums every displayed staff member; the
  // letter-derived columns (teaching/allowance/total/diff) sum only those with
  // a released letter, since they are null otherwise.
  const totals = useMemo(
    () =>
      sortedRows.reduce(
        (acc, r) => {
          acc.contractedFte += r.contractedFte;
          acc.teachingFte += r.teachingFte ?? 0;
          acc.allowanceFte += r.allowanceFte ?? 0;
          acc.totalFte += r.totalFte ?? 0;
          acc.fteDelta += r.fteDelta ?? 0;
          return acc;
        },
        { contractedFte: 0, teachingFte: 0, allowanceFte: 0, totalFte: 0, fteDelta: 0 }
      ),
    [sortedRows]
  );

  const handleSort = (key: keyof StaffFteRow) => {
    if (key === sortKey) {
      setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      // Names read best ascending; numbers most-interesting descending.
      setSortDir(key === 'staffName' || key === 'department' ? 'asc' : 'desc');
    }
  };

  const handleExport = (format: 'csv' | 'excel' | 'pdf') => {
    const data: ExportData = {
      title: 'Staff FTE (Time Fraction)',
      description: `Contracted vs effective FTE per staff member for ${year} (${semesterLabel(semester)})`,
      metadata: {
        'Generated': new Date().toLocaleDateString(),
        'Year': String(year),
        'Semester': semesterLabel(semester),
        'Learning Area': department === 'all' ? 'All Learning Areas' : department,
        'Staff': String(sortedRows.length),
        'With released letter': `${withLetters} of ${sortedRows.length}`,
      },
      columns: [
        { header: 'Staff', key: 'staffName' },
        { header: 'Code', key: 'staffCode' },
        { header: 'Learning Area', key: 'department' },
        { header: 'Employment Type', key: 'employmentType' },
        { header: 'Contracted FTE', key: 'contractedFte', format: (v) => Formatters.decimal(v, 2) },
        { header: 'Teaching FTE', key: 'teachingFte', format: fmtFte },
        { header: 'Allowance FTE', key: 'allowanceFte', format: fmtFte },
        { header: 'Total FTE (incl. allowances)', key: 'totalFte', format: fmtFte },
        { header: 'Diff (Total − Contracted)', key: 'fteDelta', format: fmtDelta },
        { header: 'Teaching Mins', key: 'teachingMinutes', format: fmtMins },
        { header: 'Allowance Mins', key: 'allowanceMinutes', format: fmtMins },
        { header: 'Allocated Mins', key: 'allocatedMinutes', format: fmtMins },
      ],
      rows: sortedRows,
    };
    exportReport(data, format);
    addToast(`Report exported as ${format.toUpperCase()}`, 'success');
  };

  return (
    <div>
      <PageHeader
        title="Staff FTE (Time Fraction)"
        description="Contracted FTE alongside the effective FTE implied by each staff member's allocated teaching load"
        actions={
          <>
            <select
              value={year ?? ''}
              onChange={e => setYear(Number(e.target.value))}
              className="border border-ink-300 rounded-md px-3 py-2 text-body-sm"
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <select
              value={semester}
              onChange={e => setSemester(e.target.value as FteSemester)}
              className="border border-ink-300 rounded-md px-3 py-2 text-body-sm"
            >
              <option value="semester1">Semester 1</option>
              <option value="semester2">Semester 2</option>
            </select>
            <select
              value={department}
              onChange={e => setDepartment(e.target.value)}
              className="border border-ink-300 rounded-md px-3 py-2 text-body-sm"
            >
              <option value="all">All Learning Areas</option>
              {departments.map(d => (
                <option key={d.id} value={d.name}>{d.name}</option>
              ))}
            </select>
            <label className="inline-flex items-center text-body-sm text-ink-700 gap-2 px-2">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={e => setActiveOnly(e.target.checked)}
              />
              Active staff only
            </label>
            <div className="relative">
              <select
                onChange={e => {
                  if (e.target.value) {
                    handleExport(e.target.value as 'csv' | 'excel' | 'pdf');
                    e.target.value = '';
                  }
                }}
                className="border border-ink-300 rounded-md px-3 py-2 bg-white text-body-sm"
                defaultValue=""
              >
                <option value="" disabled>Export</option>
                <option value="csv">CSV</option>
                <option value="excel">Excel</option>
                <option value="pdf">PDF</option>
              </select>
            </div>
          </>
        }
      />

      <Card>
        <p className="text-meta text-ink-500 mb-4">
          <strong>Contracted FTE</strong> is taken straight from each staff record.{' '}
          <strong>Teaching FTE</strong> is {semesterLabel(semester)} classroom minutes
          ÷ a full-time fortnight (2,220 min), and <strong>Allowance FTE</strong> is the
          same for allowance minutes (POL, coordination, mentoring, etc.). The two add up
          to <strong>Total FTE</strong> — each person's whole timetabled commitment — so
          there's no missing fraction: if teaching is below contract, the rest shows in
          Allowance FTE. All come from released load letters; staff without a released{' '}
          {semesterLabel(semester)} letter show "—".
        </p>

        {loading ? (
          <TableSkeleton rows={6} cols={8} />
        ) : sortedRows.length === 0 ? (
          staff.length === 0 ? (
            <EmptyState
              icon={<Users className="h-6 w-6" />}
              title="No staff records for this school yet"
              description="Staff need to be imported before this report can show anything. Changing the filters will not help."
            />
          ) : activeOnly && staff.every(s => s.status !== 'active') ? (
            <EmptyState
              icon={<Users className="h-6 w-6" />}
              title="No active staff"
              description={`${staff.length} staff on record, none of them active. Turn off “Active staff only” to include them.`}
            />
          ) : (
            <EmptyState
              icon={<Users className="h-6 w-6" />}
              title="No staff match the current filters"
              description="Try a different year, semester, or learning area, or turn off “Active staff only”."
            />
          )
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-ink-200">
              <thead className="bg-ink-50">
                <tr>
                  <Th label="Staff" onClick={() => handleSort('staffName')} />
                  <Th label="Learning Area" onClick={() => handleSort('department')} />
                  <Th label="Employment" onClick={() => handleSort('employmentType')} />
                  <Th label="Contracted FTE" onClick={() => handleSort('contractedFte')} />
                  <Th label="Teaching FTE" onClick={() => handleSort('teachingFte')} />
                  <Th label="Allowance FTE" onClick={() => handleSort('allowanceFte')} />
                  <Th label="Total FTE" onClick={() => handleSort('totalFte')} />
                  <Th label="Diff" onClick={() => handleSort('fteDelta')} />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-ink-200">
                {sortedRows.map(r => (
                  <tr key={r.staffId} className="hover:bg-ink-50">
                    <td className="px-4 py-2 text-body-sm text-ink-900">
                      {r.staffName}{' '}
                      {r.staffCode && <span className="text-ink-400 font-mono tabular-nums">({r.staffCode})</span>}
                    </td>
                    <td className="px-4 py-2 text-body-sm text-ink-500">{displayLearningArea(r.department)}</td>
                    <td className="px-4 py-2 text-body-sm text-ink-500">{r.employmentType}</td>
                    <td className="px-4 py-2 text-body-sm text-ink-700 font-mono tabular-nums">{r.contractedFte.toFixed(2)}</td>
                    <td className="px-4 py-2 text-body-sm text-ink-700 font-mono tabular-nums">{fmtFte(r.teachingFte)}</td>
                    <td className="px-4 py-2 text-body-sm text-ink-700 font-mono tabular-nums">{fmtFte(r.allowanceFte)}</td>
                    <td className="px-4 py-2 text-body-sm font-medium text-navy-700 font-mono tabular-nums">{fmtFte(r.totalFte)}</td>
                    <td className="px-4 py-2 text-body-sm font-mono tabular-nums">
                      {r.fteDelta == null ? (
                        <span className="text-ink-400">—</span>
                      ) : (
                        <span
                          className={
                            r.fteDelta > 0.01
                              ? 'text-danger-fg'
                              : r.fteDelta < -0.01
                              ? 'text-warn-fg'
                              : 'text-ink-500'
                          }
                        >
                          {fmtDelta(r.fteDelta)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-ink-50 border-t-2 border-ink-200">
                <tr>
                  <td className="px-4 py-2 text-body-sm font-medium text-ink-900" colSpan={3}>
                    Total ({withLetters} of {sortedRows.length} with a letter)
                  </td>
                  <td className="px-4 py-2 text-body-sm font-medium text-ink-900 font-mono tabular-nums">
                    {totals.contractedFte.toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-body-sm font-medium text-ink-900 font-mono tabular-nums">
                    {totals.teachingFte.toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-body-sm font-medium text-ink-900 font-mono tabular-nums">
                    {totals.allowanceFte.toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-body-sm font-semibold text-navy-700 font-mono tabular-nums">
                    {totals.totalFte.toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-body-sm font-medium text-ink-500 font-mono tabular-nums">
                    {fmtDelta(Math.round(totals.fteDelta * 100) / 100)}
                  </td>
                </tr>
              </tfoot>
            </table>
            <div className="mt-3 text-meta text-ink-500">
              {sortedRows.length} staff &middot; {withLetters} with a released{' '}
              {semesterLabel(semester)} letter &middot; letter-derived totals cover only
              those {withLetters} staff
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

const Th: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <th
    onClick={onClick}
    className="px-4 py-2 text-left text-meta font-medium text-ink-500 uppercase tracking-wider cursor-pointer select-none"
  >
    {label}
  </th>
);

export default StaffFteReport;
